import { Box } from '@mui/material';
import { ConfirmationDialog, PoolButton, PoolFormattedMessage, ValueOrNDash } from '@synergy/components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    NotificationOrigin,
    NotificationType,
    getNotificationOriginTranslationId,
    getNotificationTypeTranslationId,
} from '@synergy/models';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { NotificationSettingFormValues } from '../../models/notification-form-values';
import { NotificationsModal } from './notifications-modal';

interface NotificationsProps {
    notifications: NotificationSettingFormValues[];
    onEdit: (values: NotificationSettingFormValues) => void;
    onCreate: (values: NotificationSettingFormValues) => void;
    onDelete: (values: NotificationSettingFormValues) => void;
}

export const NotificationsTable = (props: NotificationsProps) => {
    const { notifications, onCreate, onEdit, onDelete } = props;
    const [editNotification, setEditNotification] = useState<NotificationSettingFormValues | null>(null);
    const [deleteNotification, setDeleteNotification] = useState<NotificationSettingFormValues | null>(null);
    const intl = useIntl();

    const columns: PaloColumn<NotificationSettingFormValues>[] = [
        {
            key: 'notificationType',
            // "PDF", "XML" or "XML + PDF": three name columns made the table
            // 766 pixels wide in a half of 548, and cut the address off.
            type: 'classification',
            width: '110px',
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.notificationType" />,
            cell: notification => (
                <FormattedMessage id={getNotificationTypeTranslationId(notification.notificationType)} />
            ),
        },
        {
            key: 'orderType',
            type: 'classification',
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.orderType" />,
            cell: notification => (
                <FormattedMessage id={getNotificationOriginTranslationId(notification.notificationOrigin)} />
            ),
        },
        {
            key: 'email',
            type: 'name',
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.email" />,
            cell: notification => (notification.overrideEmail ? notification.recipientEmail : <ValueOrNDash />),
        },
    ];

    const handleDelete = () => {
        if (deleteNotification) {
            onDelete(deleteNotification);
        }
        setDeleteNotification(null);
    };

    const handleSubmit = (values: NotificationSettingFormValues) => {
        if (!values.overrideEmail) {
            values.recipientEmail = '';
        }
        if (values.id) {
            onEdit(values);
        } else {
            onCreate({ ...values, id: new Date().getTime() * -1 });
        }
        setEditNotification(null);
    };

    return (
        <>
            <PaloListView<NotificationSettingFormValues, number>
                embedded={true}
                columns={columns}
                items={notifications}
                // A setting the user has just added carries a negative id already.
                getRowId={notification => notification.id ?? -1 - notifications.indexOf(notification)}
                emptyLabel={<FormattedMessage id="app.contactsAndOrganizations.detail.notifications.add" />}
                rowActions={notification => [
                    {
                        label: <FormattedMessage id="app.common.edit" />,
                        onClick: () => setEditNotification(notification),
                    },
                    {
                        label: <FormattedMessage id="app.common.delete" />,
                        onClick: () => setDeleteNotification(notification),
                    },
                ]}
            />
            <Box sx={{ ml: 1.5 }}>
                <PoolButton
                    styleType="thirdLevel"
                    startIcon={<AddIcon color="primary" />}
                    onClick={() =>
                        setEditNotification({
                            notificationType: NotificationType.EmailWithPdf,
                            notificationOrigin: NotificationOrigin.PurchaseOrderManual,
                            overrideEmail: false,
                            recipientEmail: '',
                        })
                    }
                >
                    <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.add" />
                </PoolButton>
            </Box>

            <NotificationsModal
                notification={editNotification}
                onClose={() => setEditNotification(null)}
                onSubmit={handleSubmit}
            />

            <ConfirmationDialog
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.notifications.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                isVisible={!!deleteNotification}
                onCancel={() => setDeleteNotification(null)}
                onConfirm={handleDelete}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.detail.notifications.delete.text"
                    values={{
                        notification:
                            intl.formatMessage({
                                id: getNotificationOriginTranslationId(
                                    deleteNotification?.notificationOrigin ?? NotificationOrigin.PurchaseOrderManual
                                ),
                            }) + (deleteNotification?.overrideEmail ? ` (${deleteNotification.recipientEmail})` : ''),
                    }}
                />
            </ConfirmationDialog>
        </>
    );
};
